<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// ************* import UTILS & HELPERS ************* //
import { useI18n } from 'vue-i18n'
import { FormKitSchema } from '@formkit/vue'
import type { FormKitNode } from '@formkit/core'
import useUserStore from '#/stores/userStore'
import BaseButton from '#/domains/base/components/BaseButton/BaseButton.vue'
import type { ReqSignupPayload } from '#/types/api/user'

definePageMeta({
  middleware: 'auth',
  layout: 'app-public',
  pageTransition: {
    name: 'fade-left',
    mode: 'out-in',
  },
})

const { signUp } = useUserStore()
const router = useRouter()
const { call } = useApi()
const { t } = useI18n()

// ************* FUNCTIONS | METHODS ************* //
const register = async (values: ReqSignupPayload) => {
  const res = await call(
    () => {
      return signUp(values)
    },
    {
      successMsg: t('auth.success.signup'),
      showApiError: true,
    },
  )

  if (res?.success) {
    await router.push({ name: 'auth-index-login', hash: '#signup-success' })
  }
}

const data = reactive({
  email: '',
  password: '',
  confirmedPassword: '',
})

const equal = (node: FormKitNode<Record<'password', string>>) => {
  return node.value === node.parent?.value?.password
}

const library = markRaw({
  BaseButton: BaseButton,
})

const formSchema = [
  {
    $formkit: 'form',
    classes: {
      form: 'w-[20rem]',
    },
    actions: false,
    onSubmit: register,
    children: [
      {
        $formkit: 'text',
        name: 'email',
        label: 'Email',
        validation: 'required|email',
      },
      {
        $formkit: 'password',
        name: 'password',
        label: 'Password',
        validation: 'required|length:6',
      },
      {
        $formkit: 'password',
        name: 'confirmedPassword',
        label: 'Confirm Password',
        validationMessages: {
          equal: 'Passwords do not match',
        },
        validationRules: { equal },
        validation: 'required|length:6|equal',
      },
      {
        $cmp: 'BaseButton',
        props: {
          type: 'submit',
        },
        children: ['Login'],
      },
    ],
  },
]
</script>

<template>
  <div class="flex flex-col items-center justify-center">
    
<FormKitLazyProvider config-file="true">
<FormKitSchema
      ref="formRef"
      :schema="formSchema"
      :library="library"
      :data="data"
    />

    <div class="flex items-center justify-center gap-2">
      <NuxtLink
        class="dark:text-white text-neutral-900 text-base"
        :to="{ name: 'auth-index-login' }"
      >{{ $t("Sign up") }}
      </NuxtLink>
    </div>
</FormKitLazyProvider>

  </div>
</template>
